/**
 * Drawer (Radix Dialog) — shadcn-style right-side drawer
 * For Media Devoted | Created By Ciro Autiero
 */

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';

import { cn } from '@/lib/utils';

import { ModalPortalSurfaceContext } from './ModalPortalSurfaceContext';

const Drawer = DialogPrimitive.Root;
const DrawerTrigger = DialogPrimitive.Trigger;
const DrawerPortal = DialogPrimitive.Portal;
const DrawerClose = DialogPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/50 backdrop-blur-sm',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
      className,
    )}
    {...props}
  />
));
DrawerOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface DrawerContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  width?: string;
  /** `right` = edge sheet (default). `center` = large modal dialog. */
  variant?: 'right' | 'center';
}

const layoutByVariant: Record<
  NonNullable<DrawerContentProps['variant']>,
  string
> = {
  right: cn(
    'fixed inset-y-0 right-0 z-50 flex h-full flex-col bg-background shadow-xl',
    'border-l border-border',
    'data-[state=open]:animate-in data-[state=closed]:animate-out',
    'data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
    'duration-300 ease-out',
  ),
  center: cn(
    'fixed left-1/2 top-1/2 z-50 flex max-h-[90vh] max-w-[100vw] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-border bg-background shadow-xl',
    'data-[state=open]:animate-in data-[state=closed]:animate-out',
    'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
    'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95 duration-200',
  ),
};

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DrawerContentProps
>(
  (
    {
      className,
      children,
      width = 'w-[min(960px,90vw)]',
      variant = 'right',
      ...props
    },
    ref,
  ) => {
    // Published as the nested-modal portal target: a ModalCard/ConfirmDialog
    // opened INSIDE the drawer must portal into this element, not to
    // document.body — Radix's modal FocusScope treats a body portal as
    // outside the dialog and reclaims focus, making inputs untypeable.
    const [surface, setSurface] = React.useState<HTMLDivElement | null>(null);
    const setRefs = (node: HTMLDivElement | null) => {
      setSurface(node);
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    };
    return (
      <DrawerPortal>
        <DrawerOverlay />
        <DialogPrimitive.Content
          ref={setRefs}
          className={cn(layoutByVariant[variant], width, className)}
          {...props}
        >
          <ModalPortalSurfaceContext.Provider value={surface}>
            {children}
          </ModalPortalSurfaceContext.Provider>
        </DialogPrimitive.Content>
      </DrawerPortal>
    );
  },
);
DrawerContent.displayName = 'DrawerContent';

const DrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex shrink-0 flex-col gap-1 border-b border-border bg-muted/30 px-6 py-4',
      className,
    )}
    {...props}
  />
);
DrawerHeader.displayName = 'DrawerHeader';

const DrawerBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex-1 overflow-y-auto px-6 py-4', className)} {...props} />
);
DrawerBody.displayName = 'DrawerBody';

const DrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex shrink-0 items-center justify-end gap-2 border-t border-border bg-muted/30 px-6 py-3',
      className,
    )}
    {...props}
  />
);
DrawerFooter.displayName = 'DrawerFooter';

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold text-foreground', className)}
    {...props}
  />
));
DrawerTitle.displayName = DialogPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DrawerDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Drawer,
  DrawerTrigger,
  DrawerPortal,
  DrawerClose,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
